import React, { useMemo, useState, useEffect, useRef } from 'react';
import { apiFetch } from '../utils/api';
import { useI18n } from '../hooks/useI18n';

type CategoryNode = {
    id: string;
    name: string;
    parent_id?: string | null;
    children: CategoryNode[];
};

type CategoryDropdownProps = {
    selectedCategoryId: string | null | undefined;
    onCategoryChange: (categoryId: string | null) => void;
    placeholder?: string;
    allOptionLabel?: string;
    className?: string;
    includeAllOption?: boolean;
    categoryType?: 'cost' | 'revenue';
    defaultOpen?: boolean;
    hideTrigger?: boolean;
};

const CategoryDropdown = ({
    selectedCategoryId,
    onCategoryChange,
    placeholder,
    allOptionLabel,
    className = "",
    includeAllOption = true,
    categoryType = 'cost',
    defaultOpen = false,
    hideTrigger = false
}: CategoryDropdownProps) => {
    const { t } = useI18n();
    const [hierarchicalCategories, setHierarchicalCategories] = useState<CategoryNode[]>([]);
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef<HTMLInputElement | null>(null);

    const resolvedPlaceholder = placeholder || t('categories.allCategories', 'common');
    const resolvedAllOptionLabel = allOptionLabel || resolvedPlaceholder;

    useEffect(() => {
        fetchCategories();
    }, [categoryType]);

    useEffect(() => {
        if (isOpen) {
            searchInputRef.current?.focus();
        }
    }, [isOpen]);

    const buildCategoryHierarchy = (
        categories: Array<{ id: string; name: string; parent_id?: string | null }>
    ): CategoryNode[] => {
        // Create a map for quick lookup
        const categoryMap = new Map<string, CategoryNode>();
        categories.forEach(cat => categoryMap.set(cat.id, { ...cat, children: [] }));

        // Build the hierarchy
        const rootCategories: CategoryNode[] = [];
        categories.forEach(cat => {
            const category = categoryMap.get(cat.id);
            if (!category) return;
            if (cat.parent_id && categoryMap.has(cat.parent_id)) {
                categoryMap.get(cat.parent_id)?.children.push(category);
            } else {
                rootCategories.push(category);
            }
        });

        // Sort categories at each level
        const sortCategories = (cats: CategoryNode[]) => {
            cats.sort((a, b) => a.name.localeCompare(b.name));
            cats.forEach(cat => {
                if (cat.children.length > 0) {
                    sortCategories(cat.children);
                }
            });
        };

        sortCategories(rootCategories);
        return rootCategories;
    };

    const fetchCategories = async () => {
        try {
            const endpoint = categoryType === 'revenue' ? '/api/revenue-categories/' : '/api/categories/';
            const response = await apiFetch(endpoint);
            if (response.ok) {
                const data = await response.json();
                setHierarchicalCategories(buildCategoryHierarchy(data));
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const getSelectedCategoryName = () => {
        if (selectedCategoryId === undefined) return resolvedPlaceholder;
        if (selectedCategoryId === null) return resolvedAllOptionLabel;
        const findCategory = (cats: CategoryNode[]): string | null => {
            for (const cat of cats) {
                if (cat.id === selectedCategoryId) return cat.name;
                if (cat.children.length > 0) {
                    const found = findCategory(cat.children);
                    if (found) return found;
                }
            }
            return null;
        };
        return findCategory(hierarchicalCategories) || resolvedPlaceholder;
    };

    const filterCategories = (categories: CategoryNode[], query: string): CategoryNode[] => {
        if (!query.trim()) return categories;
        const normalizedQuery = query.trim().toLowerCase();

        return categories
            .map((category) => {
                const children = filterCategories(category.children, normalizedQuery);
                const matches = category.name.toLowerCase().includes(normalizedQuery);

                if (!matches && children.length === 0) return null;

                // If parent matches, keep full subtree for faster selection in grouped branches.
                return {
                    ...category,
                    children: matches ? category.children : children,
                };
            })
            .filter((category): category is CategoryNode => Boolean(category));
    };

    const filteredCategories = useMemo(
        () => filterCategories(hierarchicalCategories, searchQuery),
        [hierarchicalCategories, searchQuery]
    );

    const renderCategoryItems = (categories: CategoryNode[], level = 0) => {
        return categories.map((category) => {
            const indentation = level * 8;

            if (category.children.length > 0) {
                // Parent category - non-selectable header
                return (
                    <div key={category.id}>
                        <div
                            className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-50 cursor-not-allowed border-b border-gray-200"
                            style={{ paddingLeft: `${8+indentation}px` }}
                        >
                            <span className="inline-flex items-center gap-1">
                                {category.name}
                            </span>
                        </div>
                        {renderCategoryItems(category.children, level + 1)}
                    </div>
                );
            } else {
                // Leaf category - selectable
                return (
                    <button
                        key={category.id}
                        className={`w-full text-left py-2 text-sm hover:bg-gray-100 transition-colors ${
                            selectedCategoryId === category.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-900'
                        }`}
                        style={{ paddingLeft: `${indentation}px` }}
                        onClick={() => {
                            onCategoryChange(category.id);
                            setIsOpen(false);
                            setSearchQuery('');
                        }}
                    >
                        <span className="inline-flex items-center gap-1">
                            <span className="text-gray-400">└</span>
                            {category.name}
                        </span>
                    </button>
                );
            }
        });
    };

    return (
        <div className={`relative ${className}`}>
            {!hideTrigger && (
                <button
                    type="button"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-left text-sm text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onClick={() => {
                        setIsOpen(!isOpen);
                        setSearchQuery('');
                    }}
                >
                    <span className="block truncate">{getSelectedCategoryName()}</span>
                    <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    </span>
                </button>
            )}

            {isOpen && (
                <div className="absolute left-0 z-1000 mt-1 w-[36rem] max-w-[90vw] bg-white border border-gray-300 rounded-md shadow-lg">
                    <div className="p-2 border-b border-gray-200">
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={t('categories.searchCategories', 'common')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div className="max-h-120 overflow-y-auto">
                    {includeAllOption && (
                        <button
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                selectedCategoryId === null ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                            }`}
                            onClick={() => {
                                onCategoryChange(null);
                                setIsOpen(false);
                                setSearchQuery('');
                            }}
                        >
                            {resolvedAllOptionLabel}
                        </button>
                    )}
                    {filteredCategories.length > 0 ? (
                        renderCategoryItems(filteredCategories)
                    ) : (
                        <div className="px-4 py-3 text-sm text-gray-500">
                            {t('categories.noCategories', 'common')}
                        </div>
                    )}
                    </div>
                </div>
            )}

            {/* Backdrop to close dropdown when clicking outside */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-0"
                    onClick={() => {
                        setIsOpen(false);
                        setSearchQuery('');
                    }}
                />
            )}
        </div>
    );
};

export default CategoryDropdown;
